import * as v from 'valibot'

export const V2_FIELDS = {
  MESSAGE: 'message',
  NAME: 'name',
  EMAIL: 'email',
  PHONE: 'phone',
  ZIP_CODE: 'zipCode',
  TARGET_BUYER: 'target_buyer',
  TERMS: 'terms_and_conditions',
  ENABLE_ALERTS: 'enable_alerts',
  BUYER_INTENTIONS: 'buyerIntentions'
}

export const MAX_CHARS_MESSAGE = 4000

export const DEFAULT_TARGET_BUYER = 'PRIVATES'

export const FIELD_ERROR_CODES = {
  MESSAGE_TOO_LONG: 'MESSAGE_TOO_LONG',
  NAME_EMPTY: 'NAME_EMPTY',
  EMAIL_EMPTY: 'EMAIL_EMPTY',
  EMAIL_INVALID: 'EMAIL_INVALID',
  PHONE_INVALID: 'PHONE_INVALID',
  ZIP_CODE_INVALID: 'ZIP_CODE_INVALID',
  TARGET_BUYER_EMPTY: 'TARGET_BUYER_EMPTY',
  TERMS_NOT_ACCEPTED: 'TERMS_NOT_ACCEPTED'
}

const isEmpty = value => !value || value.trim().length === 0

const matches = (pattern, value) => new RegExp(pattern).test(value)

export const createContactSchema = ({
  regexEmail,
  regexPhone,
  regexPostalCode,
  isZipCodeEnabled = false,
  showTargetBuyerOptions = false,
  isTermsShown = true,
  isAlertsEnabled = false
} = {}) =>
  v.object({
    [V2_FIELDS.MESSAGE]: v.pipe(
      v.string(),
      v.check(value => value.length <= MAX_CHARS_MESSAGE, FIELD_ERROR_CODES.MESSAGE_TOO_LONG)
    ),

    [V2_FIELDS.NAME]: v.pipe(
      v.string(),
      v.check(value => value.trim().length > 0, FIELD_ERROR_CODES.NAME_EMPTY)
    ),

    [V2_FIELDS.EMAIL]: v.pipe(
      v.string(),
      v.check(value => !isEmpty(value), FIELD_ERROR_CODES.EMAIL_EMPTY),
      v.check(value => isEmpty(value) || matches(regexEmail, value), FIELD_ERROR_CODES.EMAIL_INVALID)
    ),

    [V2_FIELDS.PHONE]: v.pipe(
      v.string(),
      v.check(value => isEmpty(value) || matches(regexPhone, value), FIELD_ERROR_CODES.PHONE_INVALID)
    ),

    [V2_FIELDS.ZIP_CODE]: v.pipe(
      v.string(),
      v.check(
        value => !isZipCodeEnabled || isEmpty(value) || matches(regexPostalCode, value),
        FIELD_ERROR_CODES.ZIP_CODE_INVALID
      )
    ),

    [V2_FIELDS.TARGET_BUYER]: v.pipe(
      v.string(),
      v.check(value => !showTargetBuyerOptions || !isEmpty(value), FIELD_ERROR_CODES.TARGET_BUYER_EMPTY)
    ),

    [V2_FIELDS.TERMS]: v.pipe(
      v.boolean(),
      v.check(value => !isTermsShown || value === true, FIELD_ERROR_CODES.TERMS_NOT_ACCEPTED)
    ),

    [V2_FIELDS.ENABLE_ALERTS]: v.boolean(),

    [V2_FIELDS.BUYER_INTENTIONS]: v.array(v.string())
  })

export const validateField = ({schema, name, value}) => {
  const entrySchema = schema.entries?.[name]

  if (!entrySchema) {
    return undefined
  }

  const result = v.safeParse(entrySchema, value)

  return result.success ? undefined : result.issues[0].message
}

const REQUIRED_EMPTY_CODES = [
  FIELD_ERROR_CODES.NAME_EMPTY,
  FIELD_ERROR_CODES.EMAIL_EMPTY,
  FIELD_ERROR_CODES.TARGET_BUYER_EMPTY,
  FIELD_ERROR_CODES.TERMS_NOT_ACCEPTED
]

export const hasMissingRequiredFields = ({schema, values}) =>
  Object.keys(values).some(name => REQUIRED_EMPTY_CODES.includes(validateField({schema, name, value: values[name]})))

export default createContactSchema
